import {
  AuctionSchema,
  CosmeticKindSchema,
  CrestParamsSchema,
  EntitlementsSchema,
  KitParamsSchema,
  StadiumParamsSchema,
  TitleParamsSchema,
  IsoDateTimeSchema,
  LeagueDetailSchema,
  LeagueLogEntrySchema,
  LeagueOfferSchema,
  LeagueStateSchema,
  LeagueSummarySchema,
  OfferDirectionSchema,
  UserSchema,
} from "@legacy-manager/shared";
import { z } from "zod";

import { noticiaDoMotor, rotuloDoMotor } from "../i18n";

/**
 * Esquemas das respostas que as telas do modo online consomem.
 *
 * Quase tudo já vem pronto de `@legacy-manager/shared`, e o que vem de lá é
 * usado como está. Aqui ficam duas coisas:
 *
 * As respostas **sem esquema de saída declarado na rota**, que o servidor monta
 * dentro do `service.ts`. Elas existem, o cliente depende delas, e sem um
 * esquema aqui a tela descobriria um campo faltando só ao desenhar.
 *
 * A **visão filtrada da liga**, que é o caso mais delicado. O pacote
 * compartilhado tem um `LeagueViewSchema`, mas ele não descreve o que
 * `GET /v1/leagues/:id/view` devolve hoje: a rota responde o que
 * `montarVisao` de `@legacy-manager/persistence` monta, que tem `myClub`,
 * `standings`, `news` e um corte diferente de jogador. Copiar a forma errada
 * quebraria a tela em tempo de execução, então o que está escrito abaixo é a
 * resposta de verdade.
 */

// ---------------------------------------------------------------------------
// Conta
// ---------------------------------------------------------------------------

/** `GET /v1/me` e `PATCH /v1/me`. */
export const PerfilSchema = z.object({
  user: UserSchema,
  notificationPrefs: z.record(z.boolean()),
  entitlements: EntitlementsSchema,
});
export type Perfil = z.infer<typeof PerfilSchema>;

/**
 * `GET /v1/notifications/vapid`.
 *
 * Nulo quando o servidor subiu sem par de chaves de Web Push, que é o caso de
 * qualquer instalação recém-clonada. O cliente lê isso antes de oferecer o
 * aviso: não dá para gastar o único pedido de permissão do navegador num canal
 * que ninguém configurou do outro lado.
 */
export const ChaveDeWebPushSchema = z.object({ publicKey: z.string().nullable() });

/** `POST /v1/notifications/tokens`. O token volta truncado na listagem, aqui não. */
export const TokenDePushSchema = z.object({
  token: z.string(),
  platform: z.enum(["android", "ios", "web"]),
  lastSeenAt: IsoDateTimeSchema,
});

/** `POST /v1/me/export`, respondido com 202. O arquivo chega por e-mail. */
export const ExportacaoSchema = z.object({
  status: z.literal("pending"),
  expiresAt: IsoDateTimeSchema,
});
export type Exportacao = z.infer<typeof ExportacaoSchema>;

/** `DELETE /v1/me`. O prazo de arrependimento vem pronto do servidor. */
export const ExclusaoSchema = z.object({
  status: z.literal("scheduled"),
  scheduledFor: IsoDateTimeSchema,
  cancelable: z.literal(true),
});
export type Exclusao = z.infer<typeof ExclusaoSchema>;

/** `POST /v1/me/cancel-deletion`. */
export const CancelamentoSchema = z.object({ status: z.literal("canceled") });

// ---------------------------------------------------------------------------
// Ligas
// ---------------------------------------------------------------------------

/**
 * Linha das listagens de liga.
 *
 * `inviteCode` só aparece na vitrine pública, onde ele é a porta de entrada.
 * Em `GET /v1/leagues/mine` o campo não vem, por isso é opcional.
 */
export const ResumoDeLigaSchema = LeagueSummarySchema.extend({
  inviteCode: z.string().nullable().optional(),
});
export type ResumoDeLiga = z.infer<typeof ResumoDeLigaSchema>;

export const PaginaDeLigasSchema = z.object({
  items: z.array(ResumoDeLigaSchema),
  nextCursor: z.string().nullable(),
});

/** `GET /v1/leagues/:id` e `POST /v1/leagues`. */
export const DetalheDeLigaSchema = LeagueDetailSchema.extend({
  inviteCode: z.string().nullable(),
});
export type DetalheDeLiga = z.infer<typeof DetalheDeLigaSchema>;

/** `POST /v1/leagues/:id/invites`. */
export const ConviteSchema = z.object({
  code: z.string().min(1),
  expiresAt: IsoDateTimeSchema.nullable(),
  userId: z.string().nullable(),
});

/** `POST /v1/leagues/:id/ready`. */
export const ProntoSchema = z.object({
  ready: z.boolean(),
  allReady: z.boolean(),
  round: z.number().int().min(0),
});

/** `POST /v1/leagues/:id/force-round`, respondido com 202. */
export const RodadaForcadaSchema = z.object({
  round: z.number().int().min(0),
  queued: z.boolean(),
});

/** `GET /v1/leagues/:id/log`. */
export const PaginaDeLogSchema = z.object({
  items: z.array(LeagueLogEntrySchema),
  nextCursor: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// Visão filtrada da liga
// ---------------------------------------------------------------------------

const CoresSchema = z.tuple([z.string(), z.string()]);

/** O que todo jogador da visão traz, seja do meu clube ou de outro. */
const JogadorSchema = z.object({
  id: z.string(),
  clubId: z.string().nullable(),
  name: z.string(),
  pos: z.string(),
  age: z.number(),
  strength: z.number(),
  /** Os dois atributos que mais se destacam, já traduzidos pelo motor. */
  highlights: z.array(z.string()),
  value: z.number(),
  salary: z.number(),
  contractEnd: z.number(),
});

/**
 * Jogador do meu elenco.
 *
 * Traz atributos, moral e condição. Nunca traz potencial: o teto de força só
 * aparece como faixa, dentro de um laudo de olheiro.
 */
export const JogadorDoMeuClubeSchema = JogadorSchema.extend({
  transferListed: z.boolean(),
  attrs: z.record(z.number()),
  morale: z.number(),
  condition: z.number(),
  injuryRounds: z.number(),
  suspensionRounds: z.number(),
  personality: z.string(),
  shirt: z.number().nullable(),
  onLoanFrom: z.string().nullable(),
});
export type JogadorDoMeuClube = z.infer<typeof JogadorDoMeuClubeSchema>;

/** Jogador de outro clube, na projeção pública da busca de mercado. */
export const JogadorDeOutroClubeSchema = JogadorSchema.extend({
  transferable: z.boolean(),
});
export type JogadorDeOutroClube = z.infer<typeof JogadorDeOutroClubeSchema>;

export const ClubeDaVisaoSchema = z.object({
  id: z.string(),
  name: z.string(),
  short: z.string(),
  colors: CoresSchema,
  countryId: z.string(),
  reputation: z.number(),
  division: z.number(),
  stadiumCapacity: z.number(),
  /** Clube com treinador humano. É o que separa adversário de gente. */
  managedByUser: z.boolean(),
});
export type ClubeDaVisao = z.infer<typeof ClubeDaVisaoSchema>;

export const TabelaDaVisaoSchema = z.object({
  competitionId: z.string(),
  competitionName: z.string(),
  rows: z.array(
    z.object({
      clubId: z.string(),
      position: z.number(),
      points: z.number(),
      played: z.number(),
      wins: z.number(),
      draws: z.number(),
      losses: z.number(),
      gf: z.number(),
      ga: z.number(),
    }),
  ),
});
export type TabelaDaVisao = z.infer<typeof TabelaDaVisaoSchema>;

const MeuClubeSchema = z.object({
  id: z.string(),
  name: z.string(),
  short: z.string(),
  colors: CoresSchema,
  cash: z.number(),
  debt: z.number(),
  reputation: z.number(),
  stadiumCapacity: z.number(),
  ticketPrice: z.number(),
  /**
   * O objetivo da diretoria.
   *
   * Chega como chave de rótulo do motor (`objetivo.acesso`), porque o servidor
   * não tem idioma, e sai daqui como frase. A troca acontece na borda, e não na
   * tela, pelo mesmo motivo da notícia logo abaixo: a tela do hub online está
   * sob outra mão e continua lendo `boardLabel` como sempre leu.
   */
  boardLabel: z.string().transform((chave) => rotuloDoMotor(chave)),
  boardTarget: z.number(),
  squad: z.array(JogadorDoMeuClubeSchema),
});

const ProximaPartidaSchema = z.object({
  competitionId: z.string(),
  stage: z.string(),
  round: z.number(),
  homeClubId: z.string(),
  awayClubId: z.string(),
});

/**
 * Notícia da liga, já escrita no idioma de quem está lendo.
 *
 * O servidor manda `key` e `params`, e não texto: ele não tem idioma, e a
 * mesma rodada é lida por gente que escolheu três línguas diferentes. Quem
 * escreve a frase é o dicionário, e é isso que este `transform` faz.
 *
 * Fica aqui, na borda, e não na tela, por um motivo prático: a tela do hub
 * online está sob outra mão neste momento, e deixá-la lendo `title` e `body`
 * como sempre leu é o que evita uma tela em branco enquanto ela não muda.
 * Quando ela passar a resolver a chave, este `transform` some.
 *
 * `title` e `body` continuam chegando preenchidos na notícia gravada antes do
 * esquema 8, que nunca teve chave: nesse caso o texto do servidor é o que vale.
 */
const NoticiaSchema = z
  .object({
    id: z.string(),
    kind: z.string(),
    key: z.string().nullable(),
    params: z.record(z.union([z.string(), z.number()])),
    title: z.string(),
    body: z.string(),
    round: z.number(),
  })
  .transform((noticia) => {
    const escrita = noticia.key === null ? null : noticiaDoMotor(noticia.key, noticia.params);
    if (!escrita) return noticia;
    return { ...noticia, title: escrita.titulo, body: escrita.corpo };
  });

/**
 * Laudo de olheiro da liga online.
 *
 * O resumo saiu do save: a faixa continua sendo número e o veredito virou
 * chave, então a frase é montada na tela, com `ficha.laudoResumo`, no idioma de
 * quem está lendo.
 */
const LaudoSchema = z.object({
  playerId: z.string(),
  low: z.number(),
  high: z.number(),
  confidence: z.string(),
  verdictKey: z.string(),
  notes: z.array(
    z.union([z.object({ key: z.string() }), z.object({ key: z.null(), text: z.string() })]),
  ),
});

/** `GET /v1/leagues/:id/view`, com `ETag` por rodada. */
export const VisaoDaLigaSchema = z.object({
  leagueId: z.string(),
  state: LeagueStateSchema,
  deadline: IsoDateTimeSchema.nullable(),
  schemaVersion: z.number().int(),
  season: z.number().int(),
  round: z.number().int(),
  totalRounds: z.number().int(),
  phase: z.string(),
  engineVersion: z.string(),
  /** Nulo para quem entrou como espectador e ainda não tem clube. */
  myClub: MeuClubeSchema.nullable(),
  clubs: z.array(ClubeDaVisaoSchema),
  standings: z.array(TabelaDaVisaoSchema),
  nextMatch: ProximaPartidaSchema.nullable(),
  scouting: z.array(LaudoSchema),
  news: z.array(NoticiaSchema),
  players: z.array(JogadorDeOutroClubeSchema),
});
export type VisaoDaLiga = z.infer<typeof VisaoDaLigaSchema>;

// ---------------------------------------------------------------------------
// Chat da liga
// ---------------------------------------------------------------------------

/**
 * Mensagem do chat.
 *
 * Serve para os dois caminhos: o histórico da API, que traz `displayName`, e o
 * `chat.message` do socket, que traz só o `userId`. O nome fica opcional porque
 * a tela resolve isso com a lista de membros da liga, que ela já tem.
 */
export const MensagemDoChatSchema = z.object({
  id: z.string(),
  userId: z.string().nullable(),
  displayName: z.string().nullable().optional(),
  kind: z.enum(["user", "system"]),
  text: z.string(),
  createdAt: IsoDateTimeSchema,
});
export type MensagemDoChat = z.infer<typeof MensagemDoChatSchema>;

export const PaginaDoChatSchema = z.object({
  items: z.array(MensagemDoChatSchema),
  nextCursor: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// Loja
// ---------------------------------------------------------------------------

/**
 * Catálogo, checkout e recibos.
 *
 * O servidor declara estes esquemas dentro do módulo de pagamento, e não no
 * pacote compartilhado, porque até agora nenhum cliente os consumia tipados. A
 * cópia daqui é a mesma forma, e é o que a tela de loja valida.
 */
export const TipoDeProdutoSchema = z.enum([
  "lifetime",
  "pass_month",
  "pass_year",
  "cosmetic",
  "datapack",
]);
export type TipoDeProduto = z.infer<typeof TipoDeProdutoSchema>;

export const ProdutoDaLojaSchema = z.object({
  id: z.string(),
  sku: z.string(),
  kind: TipoDeProdutoSchema,
  name: z.string(),
  /** Preço em centavos por moeda, como está em `products.prices`. */
  prices: z.record(z.number().int()),
  storeIds: z.record(z.string()),
  /** Direito que a compra concede. Nulo quando não mexe nos direitos da conta. */
  entitlement: z.string().nullable(),
  durationMonths: z.number().int().nullable(),
  /** Teto de vendas (licença de fundador) e quantas restam. `null` sem teto. */
  limit: z.number().int().positive().nullable().optional(),
  remaining: z.number().int().nonnegative().nullable().optional(),
  /**
   * Como desenhar o item, quando ele é cosmético.
   *
   * Nulo em tudo que não é cosmético, e também em cosmético que o servidor não
   * sabe desenhar. A vitrine cai para o cartão sem prévia nesse caso, em vez de
   * sumir com o produto.
   */
  cosmetic: z
    .object({
      kind: CosmeticKindSchema,
      description: z.string(),
      params: z.union([
        CrestParamsSchema,
        KitParamsSchema,
        StadiumParamsSchema,
        TitleParamsSchema,
      ]),
    })
    .nullable(),
});
export type ProdutoDaLoja = z.infer<typeof ProdutoDaLojaSchema>;

/** `GET /v1/billing/products`. */
export const CatalogoSchema = z.object({
  items: z.array(ProdutoDaLojaSchema),
  /** Meios que este servidor consegue oferecer agora. */
  methods: z.object({ card: z.boolean(), pix: z.boolean() }),
});
export type Catalogo = z.infer<typeof CatalogoSchema>;

export const MetodoDePagamentoSchema = z.enum(["card", "pix"]);
export type MetodoDePagamento = z.infer<typeof MetodoDePagamentoSchema>;

/**
 * `POST /v1/billing/checkout`.
 *
 * `expiresAt` do Pix fica como texto solto, e não como data ISO, porque é o que
 * o provedor devolve e o servidor repassa sem normalizar.
 */
export const CobrancaSchema = z.object({
  method: MetodoDePagamentoSchema,
  provider: z.string(),
  productId: z.string(),
  sku: z.string(),
  amountCents: z.number().int(),
  currency: z.string(),
  checkoutUrl: z.string().nullable(),
  pix: z
    .object({
      qrCode: z.string().nullable(),
      copyPaste: z.string(),
      expiresAt: z.string().nullable(),
    })
    .nullable(),
});
export type Cobranca = z.infer<typeof CobrancaSchema>;

export const OrigemDeCompraSchema = z.enum(["stripe", "pix", "play", "appstore", "grant"]);
export type OrigemDeCompra = z.infer<typeof OrigemDeCompraSchema>;

/** Linha de `GET /v1/billing/invoices`. */
export const ReciboSchema = z.object({
  id: z.string(),
  productId: z.string(),
  sku: z.string(),
  name: z.string(),
  source: OrigemDeCompraSchema,
  status: z.enum(["pending", "paid", "refunded", "expired"]),
  amountCents: z.number().int(),
  currency: z.string(),
  createdAt: IsoDateTimeSchema,
});
export type Recibo = z.infer<typeof ReciboSchema>;

export const PaginaDeRecibosSchema = z.object({
  items: z.array(ReciboSchema),
  nextCursor: z.string().nullable(),
});

/** `POST /v1/billing/cancel`. */
export const CancelamentoDeAssinaturaSchema = z.object({
  canceled: z.number().int(),
  activeUntil: IsoDateTimeSchema.nullable(),
  /** A loja do aparelho é quem manda, e a pessoa precisa concluir por lá. */
  manageInStore: z.boolean(),
});
export type CancelamentoDeAssinatura = z.infer<typeof CancelamentoDeAssinaturaSchema>;

// ---------------------------------------------------------------------------
// Mercado entre treinadores humanos
// ---------------------------------------------------------------------------

/**
 * Proposta como a listagem devolve.
 *
 * `LeagueOfferSchema` do pacote compartilhado descreve a linha do banco. O
 * servidor acrescenta três campos que só ele sabe: o lado em que estou nesta
 * negociação, se a transferência já entrou na fila da rodada e se ela ainda
 * espera o comissário.
 */
export const PropostaDaLigaSchema = LeagueOfferSchema.extend({
  direction: OfferDirectionSchema,
  queued: z.boolean(),
  awaitingApproval: z.boolean(),
});
export type PropostaDaLiga = z.infer<typeof PropostaDaLigaSchema>;

export const PaginaDePropostasSchema = z.object({
  items: z.array(PropostaDaLigaSchema),
  nextCursor: z.string().nullable(),
});

/** `POST /v1/leagues/:id/offers`. `auctionId` vem preenchido quando virou disputa. */
export const PropostaCriadaSchema = PropostaDaLigaSchema.extend({
  auctionId: z.string().nullable(),
});
export type PropostaCriada = z.infer<typeof PropostaCriadaSchema>;

/**
 * `POST /v1/leagues/:id/offers/:offerId/respond`.
 *
 * Os três campos opcionais só aparecem no `accept`: recusar e contrapor não
 * enfileiram transferência nenhuma, então não têm o que dizer sobre rodada.
 * `offer` pode voltar nulo na contraproposta, quando a releitura da original
 * não encontra mais a linha.
 */
export const RespostaDePropostaSchema = z.object({
  offer: PropostaDaLigaSchema.nullable(),
  counterOffer: PropostaDaLigaSchema.nullable(),
  queued: z.boolean().optional(),
  awaitingApproval: z.boolean().optional(),
  round: z.number().int().optional(),
});
export type RespostaDeProposta = z.infer<typeof RespostaDePropostaSchema>;

/** `POST /v1/leagues/:id/offers/:offerId/approve`. */
export const LiberacaoDePropostaSchema = z.object({
  offer: PropostaDaLigaSchema,
  queued: z.boolean(),
  round: z.number().int(),
});

/**
 * Leilão como a listagem devolve.
 *
 * O lance é fechado: vem quantos clubes entraram e quanto eu mesmo ofereci,
 * nunca o lance dos outros (docs/API.md 10).
 */
export const LeilaoDaLigaSchema = AuctionSchema.extend({
  bidCount: z.number().int(),
  myBid: z.number().nullable(),
});
export type LeilaoDaLiga = z.infer<typeof LeilaoDaLigaSchema>;

export const PaginaDeLeiloesSchema = z.object({
  items: z.array(LeilaoDaLigaSchema),
  nextCursor: z.string().nullable(),
});

/** `POST /v1/leagues/:id/auctions/:auctionId/bid`. */
export const LanceSchema = z.object({
  auctionId: z.string(),
  playerId: z.string(),
  clubId: z.string(),
  amount: z.number(),
  closesAtRound: z.number().int(),
});

// ---------------------------------------------------------------------------
// Ranqueado
// ---------------------------------------------------------------------------

export const DivisaoRanqueadaSchema = z.object({
  division: z.number().int(),
  name: z.string(),
  minMmr: z.number().int(),
});
export type DivisaoRanqueada = z.infer<typeof DivisaoRanqueadaSchema>;

export const EstadoDaTemporadaSchema = z.enum(["upcoming", "running", "finished"]);

/** `GET /v1/ranked/season`. */
export const TemporadaRanqueadaSchema = z.object({
  id: z.string(),
  number: z.number().int(),
  status: EstadoDaTemporadaSchema,
  startsAt: IsoDateTimeSchema,
  endsAt: IsoDateTimeSchema,
  weeks: z.number().int(),
  managersPerLeague: z.number().int(),
  divisions: z.array(DivisaoRanqueadaSchema),
});
export type TemporadaRanqueada = z.infer<typeof TemporadaRanqueadaSchema>;

/**
 * `POST /v1/ranked/queue`.
 *
 * `enqueuedAt` e `needed` só vêm de quem ficou esperando. Quando a entrada já
 * monta a liga, ou quando a fila nem chegou a receber a pessoa, os dois campos
 * não existem na resposta, por isso são opcionais e não nulos.
 */
export const FilaRanqueadaSchema = z.object({
  queued: z.boolean(),
  region: z.string().nullable(),
  mmr: z.number().int(),
  division: z.number().int(),
  position: z.number().int().nullable(),
  waiting: z.number().int(),
  enqueuedAt: IsoDateTimeSchema.optional(),
  needed: z.number().int().optional(),
  leagueId: z.string().nullable(),
});
export type FilaRanqueada = z.infer<typeof FilaRanqueadaSchema>;

const EntradaRanqueadaSchema = z.object({
  leagueId: z.string(),
  division: z.number().int(),
  mmrStart: z.number().int(),
  /** Nulo enquanto a temporada não fechou. */
  mmrEnd: z.number().int().nullable(),
  position: z.number().int().nullable(),
});

/** `GET /v1/ranked/me`. */
export const MeuRanqueadoSchema = z.object({
  mmr: z.number().int(),
  division: DivisaoRanqueadaSchema,
  season: z.object({
    id: z.string(),
    number: z.number().int(),
    startsAt: IsoDateTimeSchema,
    endsAt: IsoDateTimeSchema,
    status: EstadoDaTemporadaSchema,
  }),
  entry: EntradaRanqueadaSchema.nullable(),
  queue: z
    .object({
      region: z.string(),
      mmr: z.number().int(),
      enqueuedAt: IsoDateTimeSchema,
    })
    .nullable(),
  history: z.array(
    z.object({
      season: z.number().int(),
      startsAt: IsoDateTimeSchema,
      endsAt: IsoDateTimeSchema,
      status: EstadoDaTemporadaSchema,
      leagueId: z.string(),
      division: z.number().int(),
      mmrStart: z.number().int(),
      mmrEnd: z.number().int().nullable(),
      position: z.number().int().nullable(),
    }),
  ),
});
export type MeuRanqueado = z.infer<typeof MeuRanqueadoSchema>;

/** Linha de `GET /v1/ranked/leaderboard`. */
export const LinhaDoLeaderboardSchema = z.object({
  userId: z.string(),
  displayName: z.string(),
  /**
   * Título de treinador comprado, já conferido no servidor.
   *
   * Opcional e não com valor padrão: `default` faria a entrada do esquema
   * diferir da saída, e `pedir()` exige que as duas sejam a mesma forma.
   */
  title: TitleParamsSchema.nullish(),
  mmr: z.number().int(),
  division: z.number().int(),
  leagueId: z.string().nullable(),
  position: z.number().int().nullable(),
  mmrStart: z.number().int().nullable(),
  mmrEnd: z.number().int().nullable(),
});
export type LinhaDoLeaderboard = z.infer<typeof LinhaDoLeaderboardSchema>;

export const PaginaDoLeaderboardSchema = z.object({
  items: z.array(LinhaDoLeaderboardSchema),
  nextCursor: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// Saves na nuvem
// ---------------------------------------------------------------------------

/** Linha de `GET /v1/saves`. O blob nunca vem junto, só o metadado. */
/**
 * `GET /v1/career` e `PUT /v1/career`: a carreira da conta.
 *
 * Sem identificador, e isso é o contrato: a rota é sempre a carreira de quem
 * está com a sessão na mão, porque é uma por conta.
 */
export const CarreiraSchema = z.object({
  managerName: z.string(),
  clubId: z.string(),
  clubName: z.string(),
  season: z.number().int(),
  round: z.number().int(),
  schemaVersion: z.number().int(),
  engineVersion: z.string(),
  hash: z.string(),
  sizeBytes: z.number().int(),
  /** Quando a pessoa levou uma cópia para o modo 1 Jogador. Nunca volta de lá. */
  exportedAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Carreira = z.infer<typeof CarreiraSchema>;

/** `GET /v1/career/state` e `POST /v1/career/export`: endereço assinado, de vida curta. */
export const MundoDaCarreiraSchema = z.object({
  url: z.string(),
  expiresIn: z.number().int(),
  hash: z.string(),
  sizeBytes: z.number().int(),
});
export type MundoDaCarreira = z.infer<typeof MundoDaCarreiraSchema>;

export const SaveNaNuvemSchema = z.object({
  id: z.string(),
  name: z.string(),
  clubName: z.string(),
  season: z.number().int(),
  round: z.number().int(),
  schemaVersion: z.number().int(),
  engineVersion: z.string(),
  hash: z.string(),
  sizeBytes: z.number().int(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type SaveNaNuvem = z.infer<typeof SaveNaNuvemSchema>;

export const PaginaDeSavesNaNuvemSchema = z.object({
  items: z.array(SaveNaNuvemSchema),
  nextCursor: z.string().nullable(),
});

/** `GET /v1/saves/:id/download`: endereço assinado, de vida curta. */
export const EntregaDeSaveSchema = z.object({
  url: z.string(),
  expiresIn: z.number().int(),
  hash: z.string(),
  sizeBytes: z.number().int(),
});
export type EntregaDeSave = z.infer<typeof EntregaDeSaveSchema>;
